import { CellProps, Column, Row } from "@komune-io/g2-layout"
import { TFunction } from "i18next"
import { PlatformPaginatedTable, TableCellText } from "@tracabois/template"
import { OffsetPagination, PageQuery, PageResult } from "@tracabois/utils"
import { OrganizationRef } from "../../../../../referentiels/organizationType"
import { OrgRolesArray } from "@tracabois/connect"
import { Avatar, Box } from "@mui/material"
import { useCallback } from "react"
import { DeleteButton } from "@komune-io/g2-components"
import { RealizationId, RealizationRemoveActorCommand } from "../../../model/realizationModel"

interface RealizationActorsTableProps {
  page: PageResult<OrganizationRef>
  isLoading?: boolean
  editable?: boolean
  realizationId?: RealizationId
  onPageQueryChange: (page: PageQuery) => void
  onRowClicked?: (row: Row<OrganizationRef>) => void
  onRemoveActor?: (command: RealizationRemoveActorCommand) => Promise<boolean>
}

export const RealizationActorsTable = (props: RealizationActorsTableProps) => {
  const { page, isLoading, editable, realizationId, onPageQueryChange, onRowClicked, onRemoveActor } = props

  const onPaginationChange = (pagination: OffsetPagination) => {
    onPageQueryChange({
      pagination: pagination
    })
  }

  const onRemoveActorClicked = useCallback((organization: OrganizationRef) => {
    if (!realizationId || !onRemoveActor) return
    const command: RealizationRemoveActorCommand = {id: realizationId, organization: {id: organization.id}}
    onRemoveActor(command)
  }, [onRemoveActor, realizationId])

  const getColumns = useCallback((t: TFunction): Column<OrganizationRef>[] => {
    const columns: Column<OrganizationRef>[] = [
      {
        Header: "",
        accessor: "id",
        Cell: ({ row }: CellProps<OrganizationRef>) => (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Avatar
              sx={{ width: 32, height: 32 }}
            >
              {row.original.name?.charAt(0).toUpperCase()}
            </Avatar>
          </Box>
        ),
      },
      {
        Header: `${t("organization.name", "Nom")}`,
        accessor: "name",
        Cell: ({ row }: CellProps<OrganizationRef>) => (
          <TableCellText main={row.original.name || ""} />
        ),
      },
      {
        Header: `${t("organization.role", "Role")}`,
        accessor: "roles",
        Cell: ({ row }: CellProps<OrganizationRef>) => (
          <TableCellText
            main={row.original.roles?.filter((r) => OrgRolesArray.includes(r)).join(", ") || ""}
          />
        ),
      },
      {
        Header: `${t("organization.localisation", "Localisation")}`,
        accessor: "location",
        Cell: ({ row }: CellProps<OrganizationRef>) => (
          <TableCellText main={row.original.location || ""} />
        ),
      },
    ]

    if (onRemoveActor) {
      columns.push({
        Header: `${t("actor.remove", "Supprimer")}`,
        Cell: ({ row }: CellProps<OrganizationRef>) => (
          <DeleteButton
            onClick={(e) => {
              e.stopPropagation()
              onRemoveActorClicked(row.original)
            }}
            disabled={!editable}
          />
        ),
      })
    }

    return columns
  }, [onRemoveActor, onRemoveActorClicked, editable])

  return (
    <PlatformPaginatedTable<OrganizationRef>
      page={page}
      isLoading={isLoading}
      columnsDefinition={getColumns}
      onPageChange={onPaginationChange}
      onRowClicked={onRowClicked}
    />
  )
}
